// A Transaction Wizard (obj_type "U") embedded in a smart page, on the
// platform's surfaces (docs/plans/wizard-smart-page-embed.md):
//
//   • site mode (the platform's site viewer and the published website) — the
//     website host (`siteWizardHost.tsx`): site-txn doors + this embed's
//     page-bound token, authorized against the WIZARD's grant for the visitor;
//   • everywhere else (the page designer canvas, the Workbench page preview,
//     the Run view) — the Run view's own `PlatformWizard` over /api/livepreview.
//
// The frame around the run (launch inputs, the done panel, Start again, the
// result variable) is the shared `WizardEmbed`, the same one the downloaded
// runtime draws.

import { WizardEmbed } from '@twasta/page-runtime/wizard'
import type { WizardEmbedResult } from '@twasta/page-runtime/wizard'
import { Wand2 } from 'lucide-react'
import type { DataAccess } from '@/pages/workbench/smartpage-preview/dataBinding'
import { PlatformWizard } from '@/livepreview/runtime/wizardHost'
import { SiteWizard } from '@/livepreview/runtime/siteWizardHost'
import type { EmbedConfig } from './types'

export default function EmbedWizardBody({
  project, embed, inputs, dataAccess, onSetInput, onSaved, bodyHeight,
}: {
  project: string
  embed: EmbedConfig
  /** The launch inputs — the embed's `args` resolved against the page. */
  inputs: Record<string, unknown>
  dataAccess?: DataAccess
  onSetInput?: (name: string, value: unknown) => void
  /** A modal presentation confirms and closes on it. */
  onSaved?: () => void
  bodyHeight?: string
}) {
  if (!embed.obj_name) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '1rem 1.25rem',
        border: '1px dashed var(--pg-border, #cbd5e1)', borderRadius: 8,
        color: 'var(--pg-text-secondary, #64748b)', fontSize: 14,
      }}>
        <Wand2 size={18} aria-hidden /> Pick a wizard in the property panel to embed.
      </div>
    )
  }
  const site = dataAccess && dataAccess.mode === 'site' ? dataAccess : null
  const finished = (result: WizardEmbedResult) => {
    // A modal says "thank you" and closes — not for a run the person left.
    if (result.outcome !== 'cancelled' && result.outcome !== 'failed') onSaved?.()
  }
  return (
    <WizardEmbed
      wizard={embed.obj_name}
      inputs={inputs}
      resultVar={embed.resultVar}
      onSetVar={onSetInput}
      onFinished={finished}
      successMessage={embed.successMessage}
      height={bodyHeight}
      renderRunner={({ wizard, inputs: launch, onClose }) => (site
        ? <SiteWizard access={site} wizard={wizard} inputs={launch} onClose={onClose} />
        : <PlatformWizard project={project} wizard={wizard} inputs={launch}
            presentation="drawer" onClose={onClose} />)}
    />
  )
}
